/**
 * usePulledTrack -- given a remote track string "<sessionId>/<trackName>"
 * (what the RoomDO carries in user.tracks.{audio,video}), pull the track off
 * the SFU via partytracks and return a MediaStreamTrack to attach to a
 * <video>/<audio>. Returns undefined while pulling or when no string is given.
 *
 * Ported ~verbatim from batesky-electron cfcalls; typed for the webapp.
 */

import {useMemo} from 'react'
import {useObservableAsValue} from 'partytracks/react'
import {of} from 'rxjs'

import {type PartyTracks} from './partytracks'

export function usePulledTrack(
  partyTracks: PartyTracks | null,
  trackString: string | undefined,
): MediaStreamTrack | undefined {
  const trackData$ = useMemo(() => {
    if (!trackString) return null
    const [sessionId, trackName] = trackString.split('/')
    if (!sessionId || !trackName) return null
    return of({sessionId, trackName, location: 'remote' as const})
  }, [trackString])

  const pulled$ = useMemo(() => {
    if (!partyTracks || !trackData$) return null
    return partyTracks.pull(trackData$)
  }, [partyTracks, trackData$])

  const fallback = useMemo(() => of(undefined), [])
  return useObservableAsValue<MediaStreamTrack | undefined>(pulled$ || fallback)
}
